import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { LEGACY_TABLES, type LegacyHistoryDocDto, type LegacyHistoryListDto, type LegacyHistoryRowDto, type LegacyNamed } from "@delta/shared";
import { useLang } from "../../i18n";
import { api, ApiFailure } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { Card } from "../accounting/common";
import { when } from "./common";

// General accounts › Legacy history: the documents the migration kept as stored (migration 020), READ ONLY. There is
// no button that writes: no edit, post, reverse, settle, copy or print to a printer. Every value is shown as the legacy
// row stored it (text, its own decimals), never re-formatted, rounded or recalculated. They are history, apart from
// Delta's own invoices and vouchers, and each links to the migrated journal entries that carry its accounting effect.

const NAV = "/legacy-history";
const crumbs = (t: (k: string) => string, extra: [string, string?][] = []): [string, string?][] =>
  [[t("home"), "/home"], [t("generalAccounts")], [t("legacyHistory"), extra.length ? NAV : undefined], ...extra];
const FILTERS = ["q", "from", "to", "branchId", "type", "page"] as const;

/** A stored value, verbatim: text as it is, other JSON as JSON, an empty value as a dash. */
const raw = (v: unknown) => (v === null || v === undefined || v === "" ? <span className="muted">–</span> : <span dir="ltr" className="num">{typeof v === "string" ? v : JSON.stringify(v)}</span>);
const Badge = () => { const { t } = useLang(); return <span className="pill warn" data-testid="lh-badge"><Icon name="lock" />{t("lhBadge")}</span>; };

function Fields({ f }: { f: Record<string, string | null> }) {
  const ks = Object.keys(f);
  if (!ks.length) return <span className="muted">–</span>;
  return <span style={{ whiteSpace: "normal" }}>{ks.map((k, i) => <span key={k}>{i ? " · " : ""}<span className="muted">{k}</span> <span dir="ltr" className="num">{f[k] ?? "–"}</span></span>)}</span>;
}

export function LegacyHistory() {
  const { t, L } = useLang();
  const [sp, setSp] = useSearchParams();
  const cur = Object.fromEntries(FILTERS.map((k) => [k, sp.get(k) ?? ""])) as Record<(typeof FILTERS)[number], string>;
  const [draft, setDraft] = useState(cur);
  const [data, setData] = useState<LegacyHistoryListDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const key = sp.toString();
  const load = useCallback(async () => {
    setFailed(null); setData(null);
    const q = new URLSearchParams();
    for (const k of ["q", "from", "to", "branchId", "page"] as const) if (cur[k]) q.set(k, cur[k]);
    if (cur.type) { const [table, kind] = cur.type.split("|"); q.set("table", table!); if (kind) q.set("kind", kind); }
    try { setData(await api<LegacyHistoryListDto>("GET", `/legacy-documents?${q}`)); } catch (e) { setFailed(errorText(t, e)); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, t]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setDraft(cur); /* the address changed: Back, Forward, a link */ }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = (next: Record<string, string>) => setSp(Object.fromEntries(Object.entries(next).filter(([, v]) => v)));
  const typeLabel = (table: string, kind: string | null) => `${t(`legacyDoc_${table}`)}${kind ? ` · ${kind}` : ""}`;
  const types = (data?.kinds ?? []).map((k) => [`${k.table}|${k.kind ?? ""}`, `${typeLabel(k.table, k.kind)} (${k.count})`] as const);
  const page = data?.page ?? 1, pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  let region: ReactNode;
  if (failed) region = <div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div>;
  else if (!data) region = <div className="state-box"><p>{t("loading")}</p></div>;
  else if (!data.inOrganization) region = <div className="state-box" data-testid="lh-none"><Icon name="ledger" /><h3>{t("lhNone")}</h3><p>{t("lhNoneBody")}</p></div>;
  else if (!data.rows.length) region = <div className="state-box" data-testid="lh-no-match"><Icon name="search" /><h3>{t("lhNoMatch")}</h3><p>{t("lhNoMatchBody")}</p><button className="btn" onClick={() => go({})}>{t("clearAll")}</button></div>;
  else region = (
    <>
      <div className="table-wrap"><table className="tbl list-tbl" data-testid="lh-table">
        <thead><tr><th>{t("lhDate")}</th><th>{t("lhType")}</th><th>{t("lhNumber")}</th><th>{t("branch")}</th><th>{t("lhParty")}</th>
          <th>{t("lhStoredAmounts")}</th><th className="end">{t("lhEntries")}</th></tr></thead>
        <tbody>{data.rows.map((r: LegacyHistoryRowDto) => (
          <tr key={r.id} data-testid="lh-row">
            <td className="num">{r.documentAt ? when(r.documentAt) : "–"}</td>
            <td>{t(`legacyDoc_${r.table}`)}{r.kind && <> · <span dir="ltr">{r.kind}</span></>}</td>
            <td><Link className="num" dir="ltr" to={`${NAV}/${r.id}`} data-testid="lh-open">{r.number ?? "–"}</Link></td>
            <td>{r.branch ? L(r.branch) : <span className="muted">–</span>}</td>
            <td>{r.party?.name ?? <span className="muted">–</span>}</td>
            <td><Fields f={r.fields} /></td>
            <td className="end num">{r.entriesCount}</td>
          </tr>
        ))}</tbody>
      </table></div>
      <div className="pager">
        <span data-testid="lh-count">{t("lhShowing", { a: (page - 1) * data.pageSize + 1, b: (page - 1) * data.pageSize + data.rows.length, n: data.total })}</span><span className="spacer" />
        <button className="btn btn-sm btn-icon" disabled={page <= 1} onClick={() => go({ ...cur, page: String(page - 1) })} aria-label={t("prevPage")}><Icon name="chevR" className="rev" /></button>
        <span className="num">{page} / {pages}</span>
        <button className="btn btn-sm btn-icon" disabled={page >= pages} onClick={() => go({ ...cur, page: String(page + 1) })} aria-label={t("nextPage")} data-testid="lh-next"><Icon name="chevR" className="flip" /></button>
      </div>
    </>
  );

  const field = (k: "q" | "from" | "to", label: string, type: string, wide?: boolean) => (
    <label className={`field ${wide ? "full" : ""}`}><span className="label">{label}</span>
      <input className="control" style={{ textAlign: "start" }} type={type} value={draft[k]} data-testid={`lh-${k}`} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} /></label>
  );
  return (
    <Shell navKey={NAV}>
      <PageHead crumbs={crumbs(t)} title={t("legacyHistory")} badge={<Badge />} sub={data ? t("lhSub", { n: data.inOrganization - data.hiddenByScope }) : undefined} />
      <div className="dep-note" data-testid="lh-note"><Icon name="lock" /><span>{t("lhNote")}</span></div>
      {data && data.hiddenByScope > 0 && <div className="dep-note" data-testid="lh-hidden"><Icon name="alert" /><span>{t("lhHidden", { n: data.hiddenByScope })}</span></div>}
      <section className="card list-card">
        <form className="lsearch open" data-testid="lh-filters" onSubmit={(e) => { e.preventDefault(); go({ ...draft, page: "" }); }}>
          <div className="ls-grid">
            {field("q", t("lhSearch"), "search", true)}
            {field("from", t("fromDate"), "date")}
            {field("to", t("toDate"), "date")}
            <label className="field"><span className="label">{t("branch")}</span>
              <select className="control" value={draft.branchId} data-testid="lh-branch" onChange={(e) => setDraft({ ...draft, branchId: e.target.value })}>
                <option value="">{t("lhAllBranches")}</option>{(data?.branches ?? []).map((b) => <option key={b.id} value={b.id}>{L(b)}</option>)}
              </select></label>
            <label className="field"><span className="label">{t("lhType")}</span>
              <select className="control" value={draft.type} data-testid="lh-type" onChange={(e) => setDraft({ ...draft, type: e.target.value })}>
                <option value="">{t("lhAllTypes")}</option>{LEGACY_TABLES.flatMap((tb) => types.filter(([v]) => v.startsWith(tb + "|"))).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
          </div>
          <div className="ls-foot">
            <button type="button" className="btn btn-sm" onClick={() => { setDraft(Object.fromEntries(FILTERS.map((k) => [k, ""])) as typeof cur); go({}); }}>{t("reset")}</button>
            <button type="submit" className="btn btn-sm btn-primary" data-testid="lh-search"><Icon name="search" />{t("search")}</button>
          </div>
        </form>
        {region}
      </section>
    </Shell>
  );
}

const nm = (L: (o: LegacyNamed | null) => string, o: LegacyNamed | null) => (o ? L(o) : null);

export function LegacyHistoryShow() {
  const { t, L } = useLang();
  const { id } = useParams();
  const [d, setD] = useState<LegacyHistoryDocDto | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => {
    setD(null); setFailed(null);
    api<LegacyHistoryDocDto>("GET", `/legacy-documents/${id}`).then(setD, (e) => setFailed(e instanceof ApiFailure && e.status === 404 ? t("lhNotFound") : errorText(t, e)));
  }, [id, t]);
  if (!d) return (
    <Shell navKey={NAV}><PageHead crumbs={crumbs(t, [["…"]])} title={t("legacyHistory")} />
      <div className={`state-box ${failed ? "error" : ""}`}>{failed ? <><Icon name="alert" /><p data-testid="lh-failed">{failed}</p><Link className="btn" to={NAV}>{t("lhBack")}</Link></> : <p>{t("loading")}</p>}</div></Shell>
  );
  const title = t("lhDocTitle", { type: t(`legacyDoc_${d.table}`), x: d.number ?? "" });
  const refs: [string, string | null][] = [
    [t("branch"), nm(L, d.branch)], [t("warehouse"), nm(L, d.warehouse)], [t("lhToWarehouse"), nm(L, d.refs.toWarehouse)],
    [t("treasury"), nm(L, d.refs.treasury)], [t("lhToTreasury"), nm(L, d.refs.toTreasury)], [t("costCenter"), nm(L, d.refs.costCenter)], [t("taxType"), nm(L, d.refs.taxType)],
    [t("account"), d.refs.account ? `${d.refs.account.code ?? ""} ${L(d.refs.account)}`.trim() : null], [t("lhSecondAccount"), d.refs.secondAccount ? `${d.refs.secondAccount.code ?? ""} ${L(d.refs.secondAccount)}`.trim() : null]
  ];
  const kv: [string, ReactNode][] = [
    [t("lhType"), <>{t(`legacyDoc_${d.table}`)}{d.kind && <> · <span dir="ltr">{d.kind}</span></>}</>],
    [t("lhNumber"), <b className="num" dir="ltr" data-testid="lhd-number">{d.number ?? "–"}</b>],
    [t("lhDate"), <span className="num">{d.documentAt ? when(d.documentAt) : "–"}</span>],
    ...(d.party ? [[d.party.kind === "supplier" ? t("supplier") : t("customer"), <span data-testid="lhd-party">{d.party.name}</span>] as [string, ReactNode]] : []),
    ...refs.filter(([, v]) => v).map(([k, v]) => [k, v] as [string, ReactNode]),
    [t("lhLegacyId"), <span className="num muted" dir="ltr">{d.legacyId}</span>]
  ];
  const lineKeys = [...new Set(d.lines.flatMap((l) => Object.keys(l.data)))].filter((k) => !/^(ID|InvoiceId|AssetInvoiceId|QuotationId)$/.test(k));
  return (
    <Shell navKey={NAV}>
      <PageHead crumbs={crumbs(t, [[d.number ?? t(`legacyDoc_${d.table}`)]])} title={title} badge={<Badge />}
        actions={<Link className="btn" to={NAV}><Icon name="back" className="flip" />{t("lhBack")}</Link>} />
      <div className="dep-note" data-testid="lh-note"><Icon name="lock" /><span>{t("lhNote")}</span></div>
      <div className="inv-stack">
        <Card title={t("lhDetails")}><dl className="kv" style={{ ["--kv-cols" as string]: 3 }} data-testid="lhd-data">{kv.map(([k, x]) => <div key={k}><dt>{k}</dt><dd>{x}</dd></div>)}</dl></Card>
        <Card title={t("lhEntriesTitle")} count={d.entries.length} raw>
          {d.entries.length ? <div className="table-wrap"><table className="tbl" data-testid="lhd-entries">
            <thead><tr><th>{t("journalLegacyNumber")}</th><th>{t("dateTime")}</th><th>{t("statement")}</th><th className="end">{t("amount")}</th><th /></tr></thead>
            <tbody>{d.entries.map((e) => (
              <tr key={e.id}><td><Link className="num" dir="ltr" to={`/journals/show/${e.id}`} data-testid="lhd-entry">{e.legacyNumber ?? "–"}</Link></td><td className="num">{when(e.entryAt)}</td>
                <td><span className="cell-note">{e.statement}</span></td><td className="end num" dir="ltr">{e.total}</td>
                <td>{e.flags.map((f) => <span key={f} className="pill warn">{t(`journalFlag_${f}`)}</span>)}</td></tr>
            ))}</tbody>
          </table></div> : <div className="card-body"><p className="muted" data-testid="lhd-no-entries">{t("lhNoEntries")}</p></div>}
        </Card>
        {d.lines.length > 0 && <Card title={t("lhLinesTitle")} count={d.lines.length} raw>
          <div className="table-wrap"><table className="tbl" data-testid="lhd-lines">
            <thead><tr><th>#</th><th>{t("lhItem")}</th><th>{t("warehouse")}</th>{lineKeys.map((k) => <th key={k} dir="ltr">{k}</th>)}</tr></thead>
            <tbody>{d.lines.map((l) => (
              <tr key={l.lineNo}><td className="num">{l.lineNo}</td>
                <td>{l.item ? <>{l.item.code && <span className="num muted">{l.item.code} </span>}{L(l.item)}{L({ nameEn: l.item.unitEn, nameAr: l.item.unitAr }) && <span className="muted"> · {L({ nameEn: l.item.unitEn, nameAr: l.item.unitAr })}</span>}</> : <span className="muted">–</span>}</td>
                <td>{l.warehouse ? L(l.warehouse) : <span className="muted">–</span>}</td>
                {lineKeys.map((k) => <td key={k}>{raw(l.data[k])}</td>)}</tr>
            ))}</tbody>
          </table></div>
        </Card>}
        {d.links.length > 0 && <Card title={t("lhLinks")} count={d.links.length} raw>
          <div className="table-wrap"><table className="tbl" data-testid="lhd-links"><tbody>{d.links.map((k, i) => (
            <tr key={i}><td>{t(k.kind === "settlement" ? (k.direction === "from" ? "lhLinkSettles" : "lhLinkSettledBy") : (k.direction === "from" ? "lhLinkRefund" : "lhLinkRefundedBy"))}</td>
              <td>{k.document ? <Link to={`${NAV}/${k.document.id}`}>{t(`legacyDoc_${k.document.table}`)} <span className="num" dir="ltr">{k.document.number ?? ""}</span></Link> : <span className="muted">{t("lhOutOfScope")}</span>}</td>
              <td className="end num" dir="ltr">{k.amount ?? ""}</td></tr>
          ))}</tbody></table></div>
        </Card>}
        <Card title={t("lhOriginal")} count={Object.keys(d.data).length} raw>
          <div className="card-body"><p className="muted">{t("lhOriginalHint")}</p></div>
          <div className="table-wrap"><table className="tbl" data-testid="lhd-original">
            <thead><tr><th>{t("lhField")}</th><th>{t("lhValue")}</th></tr></thead>
            <tbody>{Object.entries(d.data).sort(([a], [b]) => (a === "ID" ? -1 : b === "ID" ? 1 : a.localeCompare(b))).map(([k, v]) => <tr key={k}><td dir="ltr">{k}</td><td>{raw(v)}</td></tr>)}</tbody>
          </table></div>
        </Card>
      </div>
    </Shell>
  );
}
